<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center">
          <h1 class="text-h4 mb-4">{{ $t('investors.title') }}</h1>
          <v-btn color="primary" @click="openAddDialog">{{ $t('investors.add') }}</v-btn>
        </div>
      </v-col>
    </v-row>
    
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>{{ $t('investors.list') }}</v-card-title>
          <v-card-text>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>{{ $t('investors.id') }}</th>
                  <th>{{ $t('investors.name') }}</th>
                  <th>{{ $t('investors.phone') }}</th>
                  <th>{{ $t('investors.email') }}</th>
                  <th>{{ $t('investors.comment') }}</th>
                  <th>{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="investor in investors" :key="investor.id">
                  <td>{{ investor.id }}</td>
                  <td><strong>{{ investor.name }}</strong></td>
                  <td>{{ investor.phone || '-' }}</td>
                  <td>{{ investor.email || '-' }}</td>
                  <td>{{ investor.comment || '-' }}</td>
                  <td>
                    <v-btn variant="plain" size="small" @click="editInvestor(investor)" icon="mdi-pencil"></v-btn>
                    <v-btn variant="plain" size="small" color="error" @click="deleteInvestor(investor.id)" icon="mdi-delete"></v-btn>
                  </td>
                </tr>
                <tr v-if="investors.length === 0">
                  <td colspan="6" class="text-center text-grey">{{ $t('investors.noInvestorsFound') }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showAddDialog" max-width="600" @update:model-value="(val) => { if (!val) resetForm(); }">
      <v-card>
        <v-card-title>{{ investorForm.id ? $t('investors.edit') : $t('investors.add') }}</v-card-title>
        <v-card-text>
          <v-form>
            <v-text-field
              v-model="investorForm.name"
              :label="$t('investors.name')"
              required
              density="compact"
              :rules="[v => !!v || $t('investors.nameRequired')]"
            />
            <v-text-field
              v-model="investorForm.phone"
              :label="$t('investors.phone')"
              density="compact"
              class="mt-2"
            />
            <v-text-field
              v-model="investorForm.email"
              :label="$t('investors.email')"
              type="email"
              density="compact"
              class="mt-2"
            />
            <v-textarea
              v-model="investorForm.comment"
              :label="$t('investors.comment')"
              rows="3"
              density="compact"
              class="mt-2"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showAddDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveInvestor">{{ investorForm.id ? $t('common.save') : $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showErrorDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6 text-error">
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          {{ $t('errors.unknownError') }}
        </v-card-title>
        <v-card-text>
          <p class="text-body-1">{{ errorMessage }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showErrorDialog = false">{{ $t('errors.ok') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api'

const { t } = useI18n()

interface Investor {
  id: number
  name: string
  phone: string | null
  email: string | null
  comment: string | null
}

const investors = ref<Investor[]>([])
const showAddDialog = ref(false)
const showErrorDialog = ref(false)
const errorMessage = ref('')
const investorForm = ref({
  id: null as number | null,
  name: '',
  phone: '',
  email: '',
  comment: ''
})

const loadInvestors = async () => {
  try {
    const response = await apiGet('/api/investors')
    if (response.ok) {
      investors.value = await response.json()
    }
  } catch (error) {
    console.error('Load investors error:', error)
  }
}

const openAddDialog = () => {
  resetForm()
  showAddDialog.value = true
}

const editInvestor = (investor: Investor) => {
  investorForm.value = {
    id: investor.id,
    name: investor.name,
    phone: investor.phone || '',
    email: investor.email || '',
    comment: investor.comment || ''
  }
  showAddDialog.value = true
}

const resetForm = () => {
  investorForm.value = {
    id: null,
    name: '',
    phone: '',
    email: '',
    comment: ''
  }
}

const showError = (message: string) => {
  errorMessage.value = message
  showErrorDialog.value = true
}

const saveInvestor = async () => {
  if (!investorForm.value.name) {
    showError(t('investors.fillName'))
    return
  }

  try {
    const investorData = {
      name: investorForm.value.name,
      phone: investorForm.value.phone || null,
      email: investorForm.value.email || null,
      comment: investorForm.value.comment || null
    }

    const url = investorForm.value.id ? `/api/investors/${investorForm.value.id}` : '/api/investors'
    const response = investorForm.value.id
      ? await apiPut(url, { investor: investorData })
      : await apiPost(url, { investor: investorData })

    if (response.ok) {
      await loadInvestors()
      showAddDialog.value = false
      resetForm()
    } else {
      const errorData = await response.json()
      const errorText = errorData.errors?.join(', ') || t('errors.unknownError')
      showError(t('investors.failedToSave', { errors: errorText }))
    }
  } catch (error) {
    console.error('Save investor error:', error)
    const errorText = error instanceof Error ? error.message : t('investors.errorSaving', { errors: error.message })
    showError(t('investors.errorSaving', { errors: errorText }))
  }
}

const deleteInvestor = async (id: number) => {
  if (!confirm(t('investors.areYouSureDelete'))) return
  try {
    const response = await apiDelete(`/api/investors/${id}`)
    if (response.ok) {
      await loadInvestors()
    } else {
      showError(t('investors.failedToDelete'))
    }
  } catch (error) {
    console.error('Delete investor error:', error)
    showError(t('investors.errorDeleting'))
  }
}

onMounted(() => {
  loadInvestors()
})
</script>
